import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Calculator, Eye, Send, Pencil, Check, X, ExternalLink, Users, MinusCircle, Undo2, Loader2 } from 'lucide-react';
import { driversApi, paystubOverridesApi, paystubEmailsApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { DashCard, money } from '../dashboard/DashCard';
import { PayCalculatorDialog } from './PayCalculatorDialog';
import { PdfPreviewDialog } from './PdfPreviewDialog';
import { EmailPromptDialog } from './EmailPromptDialog';
import { DriverInfoDialog } from './DriverInfoDialog';

const GrossCell = ({ driver, s, weekStart, onChanged, compact = false }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState('');
  const save = async () => {
    try {
      await paystubOverridesApi.upsert({ person_type: 'driver', person_id: driver.id, week_start: weekStart, override_amount: Number(val || 0), notes: 'Edited on Summary' });
      toast.success('Gross pay updated');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };
  const clear = async () => {
    try {
      await paystubOverridesApi.delete('driver', driver.id, weekStart);
      toast.success('Back to calculated pay');
      onChanged();
    } catch (e) { toast.error('Could not undo the edit'); }
  };
  if (editing) {
    return (
      <div className={`flex items-center gap-1 ${compact ? 'justify-start' : 'justify-end'}`}>
        <Input autoFocus type="number" step="0.01" className="h-9 w-28 text-right" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`dp-gross-input-${driver.id}`} />
        <Button size="icon" className="h-9 w-9 bg-blue-600 hover:bg-blue-700" onClick={save} aria-label="Save gross pay" data-testid={`dp-gross-save-${driver.id}`}><Check className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setEditing(false)} aria-label="Cancel"><X className="h-4 w-4" /></Button>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-1.5 ${compact ? 'flex-wrap justify-start' : 'justify-end'}`}>
      <span className="font-mono font-semibold text-base text-slate-900 dark:text-white" data-testid={`dp-gross-${driver.id}`}>{s ? money(s.gross_pay) : '—'}</span>
      <Button size="sm" variant="ghost" className="h-8 px-2 text-blue-700 dark:text-blue-300" onClick={() => { setVal(String(s?.gross_pay ?? '')); setEditing(true); }} data-testid={`dp-gross-edit-${driver.id}`}>
        <Pencil className="h-3.5 w-3.5 mr-1" />Edit
      </Button>
      {s?.is_override && <button className="text-xs text-blue-700 dark:text-blue-300 underline" onClick={clear} data-testid={`dp-gross-clear-${driver.id}`}>undo</button>}
    </div>
  );
};

const Actions = ({ d, s, busy, weekStart, onCalc, onPreview, onSend, onDeduct, onUndoDeduct }) => (
  <div className="flex flex-wrap justify-end gap-1.5">
    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={onCalc} disabled={!d.assigned_unit_id} data-testid={`dp-calc-${d.id}`}><Calculator className="h-4 w-4 mr-1" />Calculate</Button>
    <Button size="sm" variant="outline" onClick={onPreview} disabled={busy === `pdf-${d.id}`} data-testid={`dp-preview-${d.id}`}>
      {busy === `pdf-${d.id}` ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}Preview
    </Button>
    <Button size="sm" variant="outline" onClick={onSend} disabled={busy === `email-${d.id}`} data-testid={`dp-email-${d.id}`}>
      {busy === `email-${d.id}` ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Send
    </Button>
    {s?.unit_deduction ? (
      <>
        {Math.abs((s.unit_deduction.amount || 0) - (s.gross_pay || 0)) > 0.009 && (
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" onClick={onDeduct} title="Driver pay changed since it was deducted — update the unit deduction" data-testid={`dp-update-deduct-${d.id}`}>
            <MinusCircle className="h-4 w-4 mr-1" />Update deduction to {money(s.gross_pay)}
          </Button>
        )}
        <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 dark:text-blue-300 dark:border-blue-700" onClick={onUndoDeduct} title="Remove the driver pay deduction from the unit" data-testid={`dp-undo-deduct-${d.id}`}>
          <Undo2 className="h-4 w-4 mr-1" />Deducted {money(s.unit_deduction.amount)} · Undo
        </Button>
      </>
    ) : (
      <Button size="sm" variant="outline" onClick={onDeduct} disabled={!d.assigned_unit_id || !s?.gross_pay} title="Add the driver's gross pay as a deduction on their unit's settlement" data-testid={`dp-deduct-${d.id}`}>
        <MinusCircle className="h-4 w-4 mr-1" />Deduct from unit
      </Button>
    )}
    <Link to={`/paystubs?driver=${d.id}&week=${weekStart}`}><Button size="sm" variant="ghost" title="Bonuses, deductions & more" data-testid={`dp-open-${d.id}`}><ExternalLink className="h-4 w-4 mr-1" />Full paystub</Button></Link>
  </div>
);

const Badges = ({ d, s }) => (
  <div className="flex flex-wrap gap-1 mt-1">
    {s?.pay_calc && <span className="text-[11px] px-1.5 py-0.5 rounded bg-blue-600 text-white" data-testid={`dp-badge-calc-${d.id}`}>Calculated · {s.pay_calc.lines} loads</span>}
    {s?.is_override && <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-700 text-white" data-testid={`dp-badge-override-${d.id}`}>Edited</span>}
  </div>
);

const NameBtn = ({ d, onOpen }) => (
  <button type="button" onClick={() => onOpen(d)} className="font-semibold text-base text-blue-700 dark:text-blue-300 hover:underline text-left" data-testid={`dp-name-${d.id}`}>{d.name}</button>
);

/** Summary → Driver Paystubs tab: every active driver's live pay for the week, editable. */
export const DriverPaystubsTab = ({ weekStart, unitFilter = 'all' }) => {
  const [drivers, setDrivers] = useState([]);
  const [sums, setSums] = useState({});
  const [loading, setLoading] = useState(true);
  const [calcDriver, setCalcDriver] = useState(null);
  const [infoDriver, setInfoDriver] = useState(null);
  const [emailDriver, setEmailDriver] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, ps] = await Promise.all([driversApi.getAll(), driversApi.getPaySummaries(weekStart)]);
      setDrivers(dr.data.filter((d) => d.status === 'active'));
      setSums(ps.data || {});
    } catch (e) { toast.error('Could not load driver paystubs'); } finally { setLoading(false); }
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);

  const visible = unitFilter === 'all' ? drivers : drivers.filter((d) => d.assigned_unit_id === unitFilter);

  const preview = async (d) => {
    setBusy(`pdf-${d.id}`);
    try {
      const res = await driversApi.getPaystub(d.id, weekStart);
      setPdf({ driver: d, url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })) });
    } catch (e) { toast.error('Could not generate paystub'); } finally { setBusy(null); }
  };
  const closePdf = () => { if (pdf?.url) URL.revokeObjectURL(pdf.url); setPdf(null); };

  const sendTo = async (d, email = null) => {
    setBusy(`email-${d.id}`);
    try {
      const res = await paystubEmailsApi.send('driver', d.id, weekStart, email, '', !!email);
      toast.success(`Paystub emailed to ${res.data?.sent_to || d.name}`);
      if (email) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Email failed');
      throw e;
    } finally { setBusy(null); }
  };
  const send = (d) => {
    if (!d.email) { setEmailDriver(d); return; }
    sendTo(d).catch(() => {});
  };
  const deduct = async (d) => {
    try {
      const res = await driversApi.deductFromUnit(d.id, weekStart);
      toast.success(`${res.data.updated ? 'Updated' : 'Deducted'} ${money(res.data.amount)} on Unit ${d.assigned_unit_number}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Deduct failed'); }
  };
  const undoDeduct = async (d) => {
    try { await driversApi.undoDeductFromUnit(d.id, weekStart); toast.success('Unit deduction removed'); load(); }
    catch (e) { toast.error('Could not remove deduction'); }
  };

  const totals = visible.reduce((t, d) => {
    const s = sums[d.id];
    return s ? { gross: t.gross + (s.gross_pay || 0), ded: t.ded + (s.total_deductions || 0), net: t.net + (s.net_pay || 0) } : t;
  }, { gross: 0, ded: 0, net: 0 });

  const actionProps = (d) => ({
    d, s: sums[d.id], busy, weekStart,
    onCalc: () => setCalcDriver(d), onPreview: () => preview(d), onSend: () => send(d),
    onDeduct: () => deduct(d), onUndoDeduct: () => undoDeduct(d),
  });

  return (
    <DashCard title="Driver Paystubs" subtitle={`Active drivers · week of ${weekStart}${unitFilter !== 'all' ? ' · filtered by unit' : ''}`} icon={Users} testid="driver-paystubs-tab"
      action={<div className="text-sm text-slate-600 dark:text-slate-300 text-right">Total net <span className="block sm:inline font-mono font-semibold text-slate-900 dark:text-white" data-testid="dp-total-net">{money(totals.net)}</span></div>}>
      {loading ? <Skeleton className="h-64 w-full rounded-xl" /> : visible.length === 0 ? (
        <div className="py-10 text-center text-base text-slate-600 dark:text-slate-300" data-testid="dp-empty">No active driver assigned to this unit</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden xl:block">
            <table className="w-full text-sm" data-testid="driver-paystubs-table">
              <thead>
                <tr className="text-left text-slate-600 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-3 pr-3 font-medium">Driver</th>
                  <th className="py-3 pr-3 font-medium">Pay basis</th>
                  <th className="py-3 pr-3 font-medium text-right">Loads</th>
                  <th className="py-3 pr-3 font-medium text-right">Gross pay</th>
                  <th className="py-3 pr-3 font-medium text-right">Deductions</th>
                  <th className="py-3 pr-3 font-medium text-right">Net pay</th>
                  <th className="py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => {
                  const s = sums[d.id];
                  return (
                    <tr key={d.id} className="border-b border-blue-100 dark:border-slate-800 align-top odd:bg-blue-50/50 dark:odd:bg-slate-800/40 hover:bg-blue-50 dark:hover:bg-slate-800/70 transition-colors" data-testid={`dp-row-${d.id}`}>
                      <td className="py-3.5 pr-3">
                        <NameBtn d={d} onOpen={setInfoDriver} />
                        <div className="text-sm text-slate-500 dark:text-slate-400">{d.assigned_unit_number ? `Unit ${d.assigned_unit_number}` : 'No unit'}</div>
                      </td>
                      <td className="py-3.5 pr-3"><div className="text-slate-700 dark:text-slate-200">{s?.pay_basis || '—'}</div><Badges d={d} s={s} /></td>
                      <td className="py-3.5 pr-3 text-right font-mono">{s?.load_count ?? '—'}</td>
                      <td className="py-3.5 pr-3"><GrossCell driver={d} s={s} weekStart={weekStart} onChanged={load} /></td>
                      <td className="py-3.5 pr-3 text-right font-mono text-slate-600 dark:text-slate-300">{s ? `${s.total_deductions ? '−' : ''}${money(s.total_deductions)}` : '—'}</td>
                      <td className={`py-3.5 pr-3 text-right font-mono font-bold text-base ${s?.net_pay < 0 ? 'text-rose-600' : 'text-blue-700 dark:text-blue-300'}`} data-testid={`dp-net-${d.id}`}>{s ? money(s.net_pay) : '—'}</td>
                      <td className="py-3.5 max-w-[420px]"><Actions {...actionProps(d)} /></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-3" colSpan={3}>Totals</td>
                  <td className="py-3 pr-3 text-right font-mono">{money(totals.gross)}</td>
                  <td className="py-3 pr-3 text-right font-mono text-slate-600 dark:text-slate-300">{totals.ded ? '−' : ''}{money(totals.ded)}</td>
                  <td className="py-3 pr-3 text-right font-mono">{money(totals.net)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile / tablet cards */}
          <div className="xl:hidden space-y-3" data-testid="driver-paystubs-cards">
            {visible.map((d) => {
              const s = sums[d.id];
              return (
                <div key={d.id} className="rounded-xl border border-blue-200/80 dark:border-blue-900/60 bg-gradient-to-br from-blue-100/70 to-blue-50/50 dark:from-slate-800 dark:to-slate-900 shadow-sm p-4" data-testid={`dp-card-${d.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <NameBtn d={d} onOpen={setInfoDriver} />
                      <div className="text-sm text-slate-500 dark:text-slate-400">{d.assigned_unit_number ? `Unit ${d.assigned_unit_number}` : 'No unit'} · {s?.load_count ?? 0} loads</div>
                      <div className="text-sm text-slate-700 dark:text-slate-200 mt-0.5">{s?.pay_basis || '—'}</div>
                      <Badges d={d} s={s} />
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Net pay</div>
                      <div className={`font-mono font-bold text-lg ${s?.net_pay < 0 ? 'text-rose-600' : 'text-blue-700 dark:text-blue-300'}`}>{s ? money(s.net_pay) : '—'}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    <div className="rounded-lg bg-white/80 dark:bg-slate-900/60 border border-blue-100 dark:border-slate-700 p-2.5">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Gross pay</div>
                      <GrossCell driver={d} s={s} weekStart={weekStart} onChanged={load} compact />
                    </div>
                    <div className="rounded-lg bg-white/80 dark:bg-slate-900/60 border border-blue-100 dark:border-slate-700 p-2.5">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Deductions</div>
                      <div className="font-mono font-semibold text-base text-slate-700 dark:text-slate-200">{s ? `${s.total_deductions ? '−' : ''}${money(s.total_deductions)}` : '—'}</div>
                    </div>
                  </div>
                  <div className="mt-3 [&>div]:justify-start"><Actions {...actionProps(d)} /></div>
                </div>
              );
            })}
          </div>
        </>
      )}
      <PayCalculatorDialog open={!!calcDriver} onOpenChange={(o) => !o && setCalcDriver(null)} driver={calcDriver} weekStart={weekStart} onSaved={load} />
      <DriverInfoDialog driver={infoDriver} summary={infoDriver ? sums[infoDriver.id] : null} onOpenChange={(o) => !o && setInfoDriver(null)} />
      <EmailPromptDialog
        open={!!emailDriver}
        onOpenChange={(o) => !o && setEmailDriver(null)}
        title={`Email ${emailDriver?.name || 'driver'}'s paystub`}
        description="No email on file for this driver. It will be saved to their profile."
        submitLabel="Send & save"
        onSubmit={(email) => sendTo(emailDriver, email)}
      />
      <PdfPreviewDialog
        open={!!pdf}
        onOpenChange={(o) => !o && closePdf()}
        url={pdf?.url}
        title={pdf ? `Paystub · ${pdf.driver.name} · week of ${weekStart}` : ''}
        fileName={pdf ? `Paystub_${pdf.driver.name.replace(/\s+/g, '_')}_${weekStart}.pdf` : undefined}
        sending={pdf && busy === `email-${pdf.driver.id}`}
        onSend={pdf ? () => send(pdf.driver) : undefined}
      />
    </DashCard>
  );
};
